import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {DecimalPipe} from "@angular/common";

@Directive({selector: '[NumbersOnly]'})
export class NumbersOnly {

}

@Directive({
  selector: '[mesNumberInput]'
})
export class NumberInputDirective implements OnInit, OnChanges {
  @Input()
  mesNumberInput?: number | string;

  @Input()
  format: string;

  @Output() mesNumberInputChange: EventEmitter<number> = new EventEmitter();
  @Output() onChange: EventEmitter<number> = new EventEmitter();

  @Input() allowDecimals: boolean = true;
  @Input() allowSign: boolean = false;
  @Input() decimalPlaces?: number;

  previousValue: string = '';

  // --------------------------------------
  //  Regular expressions
  integerUnsigned: string = '^[0-9]*$';
  integerSigned: string = '^-?[0-9]+$';
  decimalUnsigned: string = '^[0-9]+((,|.)[0-9]+)?$';
  decimalSigned: string = '^-?[0-9]+((,|.)[0-9]+)?$';
  private input: HTMLInputElement;
  initialValue: number;

  constructor(private decimalPipe: DecimalPipe, private elementRef: ElementRef, private changeDetector: ChangeDetectorRef) {
    this.input = (this.elementRef.nativeElement as HTMLInputElement);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.mesNumberInput) {
      if (!changes.mesNumberInput.isFirstChange()) {
        let currentValue = changes.mesNumberInput.currentValue;
        if (currentValue == null) {
          currentValue = NaN;
        }
        this.validateValue(currentValue.toString());
        this.formatANumber(currentValue.toString());
      }
    }
  }

  /**
   * Event handler for host's change event
   * @param e
   */
  // @HostListener('change', ['$event']) onChange(e) {
  //   this.validateValue(e.target.value);
  //   this.formatANumber(e.target.value);
  // }

  @HostListener('blur', ['$event'])
  onBlur() {
    if (!this.input.readOnly) {
      this.validateValue(this.input.value);
      this.formatANumber(this.input.value);
      if (this.initialValue !== this.getNumber(this.input.value)) {
        this.onChange.emit(this.getNumber(this.input.value));
      }
    }
  }

  /**
   * Event handler for host's paste event
   * @param e
   */
  @HostListener('paste', ['$event']) onPaste(e) {
    // get and validate data from clipboard
    let value = e.clipboardData.getData('text/plain');

    const selection = this.input.selectionStart;
    if (selection > 0) {
      const original = this.input.value;
      const start = original.substring(0, selection);
      const end = original.substring(selection);
      value = start + value + end;
    }

    this.validateValue(value);
    this.formatANumber(value);
    e.preventDefault();
  }

  @HostListener('focus', ['$event.target.value'])
  cleanUp() {
    if (!this.input.readOnly) {
      if (this.input.value) {
        this.input.value = this.input.value.replace(/\s/g, '');
        this.initialValue = this.getNumber(this.input.value);
      }
    }
  }

  /**
   * Event handler for host's keydown event
   * @param event
   */
  @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
    let cursorPosition: number = e.target['selectionStart'];
    let originalValue: string = e.target['value'];
    let key: string = this.getName(e);
    let controlOrCommand = (e.ctrlKey === true || e.metaKey === true);
    let signExists = originalValue.includes('-');
    let separatorExists = originalValue.includes(',') || originalValue.includes('.');

    if (key === 'Enter') {
      this.onBlur();
    }

    // allowed keys apart from numeric characters
    let allowedKeys = [
      'Backspace', 'ArrowLeft', 'ArrowRight', 'Escape', 'Tab', 'Delete'
    ];

    // when decimals are allowed, add
    // decimal separator to allowed codes when
    // its position is not close to the the sign (-. and .-)
    let separatorIsCloseToSign = (signExists && cursorPosition <= 1);
    if (this.allowDecimals && !separatorIsCloseToSign && !separatorExists) {
      allowedKeys.push(...['.', ',']);
    }

    // when minus sign is allowed, add its
    // key to allowed key only when the
    // cursor is in the first position, and
    // first character is different from
    // decimal separator
    let firstCharacterIsSeparator = (originalValue.charAt(0) != '.') || (originalValue.charAt(0) != ',');
    if (this.allowSign && !signExists && firstCharacterIsSeparator && cursorPosition == 0) {
      allowedKeys.push('-');
    }

    // allow some non-numeric characters
    if (allowedKeys.indexOf(key) != -1 ||
      // Allow: Ctrl+A and Command+A
      (key == 'a' && controlOrCommand) ||
      // Allow: Ctrl+C and Command+C
      (key == 'c' && controlOrCommand) ||
      // Allow: Ctrl+V and Command+V
      (key == 'v' && controlOrCommand) ||
      // Allow: Ctrl+X and Command+X
      (key == 'x' && controlOrCommand)) {
      // let it happen, don't do anything
      return;
    }

    // save value before keydown event
    this.previousValue = originalValue;

    // allow number characters only
    let isNumber = (new RegExp(this.integerUnsigned)).test(key);
    if (isNumber) return; else e.preventDefault();
  }

  @HostListener('change', ['$event'])
  onKeyUp(e: KeyboardEvent) {
    this.onChange.emit(this.getNumber(this.input.value));
  }

  ngOnInit() {
    this.input.classList.add('number-input');
    this.input.value = this.getDecimalValue(this.mesNumberInput);
  }

  /**
   * Test whether value is a valid number or not
   * @param value
   */
  validateValue(value: string): void {

    // choose the appropiate regular expression
    let regex: string;
    if (!this.allowDecimals && !this.allowSign) regex = this.integerUnsigned;
    if (!this.allowDecimals && this.allowSign) regex = this.integerSigned;
    if (this.allowDecimals && !this.allowSign) regex = this.decimalUnsigned;
    if (this.allowDecimals && this.allowSign) regex = this.decimalSigned;

    value = value.replace(/\s/g, '').replace(/\./g, ',');

    // when a numbers begins with a decimal separator,
    // fix it adding a zero in the beginning
    let firstCharacter = value.charAt(0);
    if (firstCharacter == '.' || firstCharacter == ',')
      value = 0 + value;

    // when a numbers ends with a decimal separator,
    // fix it adding a zero in the end
    let lastCharacter = value.charAt(value.length - 1);
    if (lastCharacter == '.' || lastCharacter == ',')
      value = value + 0;

    // test number with regular expression, when
    // number is invalid, replace it with a zero
    let valid: boolean = (new RegExp(regex)).test(value);
    this.elementRef.nativeElement.value = valid ? value : null;
  }

  /**
   * Get key's name
   * @param e
   */
  getName(e): string {
    if (e.key) {
      return e.key;
    } else {
      // for old browsers
      if (e.keyCode && String.fromCharCode) {

        switch (e.keyCode) {
          case   8:
            return 'Backspace';
          case   9:
            return 'Tab';
          case  27:
            return 'Escape';
          case  37:
            return 'ArrowLeft';
          case  39:
            return 'ArrowRight';
          case 188:
            return ',';
          case 190:
            return '.';
          case 109:
            return '-'; // minus in numbpad
          case 173:
            return '-'; // minus in alphabet keyboard in firefox
          case 189:
            return '-'; // minus in alphabet keyboard in chrome
          default:
            return String.fromCharCode(e.keyCode);
        }
      }
    }
  }

  formatANumber(value) {
    const numberValue = this.stringToNumber(value);
    if (isNaN(numberValue)) {
      this.mesNumberInputChange.next(null);
    } else {
      this.input.value = this.getDecimalValue(numberValue);
      this.mesNumberInputChange.next(this.stringToNumber(this.input.value));
      this.changeDetector.detectChanges();
    }
  }

  private getFormat() {
    if (this.format)
      return this.format;

    let places = 0;
    if (this.allowDecimals) {
      places = 2;
      if (this.decimalPlaces != null) {
        places = this.decimalPlaces;
      }
    }

    return `1.${places}-${places}`;
  }

  private stringToNumber(value: string) {
    let asNumber = value.replace(/\s/g, '').replace('.', ',').replace(',', '.').replace('−', '-');
    return parseFloat(asNumber);
  }

  private getNumber(value: any): number | null {
    const numberValue = this.stringToNumber(value);
    if (isNaN(numberValue)) {
      return null;
    } else {
      const trans = this.getDecimalValue(numberValue);
      return this.stringToNumber(trans);
    }
  }

  private getDecimalValue(numberValue: number | string) {
    let s = this.decimalPipe.transform(numberValue, this.getFormat());
    if (s) {
      return s.replace('−', '-');
    }
    return s;
  }
}


